import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, Bot, PlayCircle } from "lucide-react";

const AGENTS_BY_INDUSTRY: Record<string, string[]> = {
  "E-commerce & Retail": [
    "Product Listing Validator","Inventory Reorder Alert Bot","Customer Return Processor",
    "Review Sentiment Analyzer","Abandoned Cart Recovery Agent","Price Competitor Monitor",
    "Visual Search Agent","Loyalty Points Calculator","Shipping Delay Notifier",
    "Seasonal Promotion Scheduler","Cross-Sell Recommendation Engine","Product Description Generator",
    "Flash Sale Coordinator","Size Guide Recommender","Fraud Order Detector",
    "Catalog Sync Automator","Wishlist Restock Notifier","Bundle Pricing Optimizer",
    "Seller Onboarding Bot","Return Rate Analyzer",
  ],
  "Healthcare": [
    "Appointment Reminder Agent","Patient Intake Form Processor","Insurance Pre-Authorization Bot",
    "Medical Record Summarizer","Medication Refill Reminder","Lab Result Notifier",
    "Symptom Triage Chatbot","HIPAA Compliance Checker","Clinical Trial Matcher",
    "Doctor Referral Coordinator","Discharge Summary Generator","Prior Auth Status Tracker",
    "Patient No-Show Predictor","Care Gap Identifier","Billing Code Validator",
    "Patient Satisfaction Surveyor","Staff Scheduling Optimizer","ICD Code Suggester",
    "Chronic Disease Monitor","Telehealth Follow-Up Coordinator",
  ],
  "Finance & Banking": [
    "Expense Categorization Agent","Fraud Detection Alert Bot","Invoice Processing Automator",
    "Budget Variance Reporter","Tax Document Organizer","Credit Score Monitor",
    "Loan Application Screener","Financial Report Summarizer","Currency Exchange Rate Tracker",
    "Payroll Exception Handler","Transaction Anomaly Detector","Customer Onboarding KYC Bot",
    "Portfolio Rebalancing Alerter","Account Overdraft Predictor","Regulatory Filing Reminder",
    "Interest Rate Change Notifier","Wire Transfer Verifier","Debt Collection Scheduler",
    "Branch Performance Reporter","Mortgage Pre-Qualification Bot",
  ],
  "Real Estate": [
    "Property Listing Optimizer","Lead Qualification Bot","Lease Renewal Reminder",
    "Maintenance Request Router","Market Comparable Analyzer","Virtual Tour Scheduler",
    "Tenant Screening Agent","Rental Price Monitor","Document Signature Tracker",
    "HOA Violation Detector","Open House Coordinator","Commission Calculator",
    "Property Tax Alert Bot","Investor ROI Analyzer","Eviction Notice Preparer",
    "Listing Photo Enhancer","Neighborhood Report Generator","Utility Setup Coordinator",
    "Move-In Checklist Automator","Commercial Lease Abstractor",
  ],
  "Marketing & Sales": [
    "Lead Enrichment Scraper","Cold Email Personalization Agent","Social Media Content Scheduler",
    "Ad Performance Analyzer","CRM Data Cleanup Bot","Competitor Mention Monitor",
    "SEO Keyword Tracker","Campaign ROI Calculator","Influencer Outreach Agent",
    "Sales Call Summary Writer","Pipeline Stagnation Alerter","A/B Test Result Reporter",
    "Webinar Follow-Up Automator","Ideal Customer Profile Builder","Testimonial Request Bot",
    "Win/Loss Analysis Agent","Proposal Generator","Demo Scheduler",
    "Newsletter Personalization Bot","UTM Parameter Auditor",
  ],
  "HR & Recruiting": [
    "Resume Screening Agent","Interview Scheduling Bot","Onboarding Checklist Automator",
    "Employee Sentiment Tracker","Job Description Generator","Payroll Anomaly Detector",
    "PTO Balance Calculator","Candidate Pipeline Reporter","Performance Review Summarizer",
    "Exit Interview Analyzer","Offer Letter Generator","Employee Birthday Notifier",
    "Diversity Hiring Tracker","Training Completion Monitor","Background Check Coordinator",
    "Headcount Forecast Agent","Benefits Enrollment Reminder","Org Chart Updater",
    "Workforce Attrition Predictor","Compliance Training Scheduler",
  ],
  "Legal": [
    "Contract Review Agent","NDA Comparison Bot","Deadline & Filing Tracker",
    "Case Summary Generator","Compliance Policy Checker","Discovery Document Organizer",
    "Legal Invoice Auditor","Jurisdiction Research Agent","Client Intake Automator",
    "Trademark Conflict Checker","Deposition Summary Writer","Statute of Limitations Alerter",
    "Court Date Calendar Bot","Legal Research Compiler","Billing Rate Validator",
    "Matter Status Reporter","Retainer Balance Monitor","Regulatory Change Alerter",
    "E-Signature Workflow Bot","Privilege Log Generator",
  ],
  "Education": [
    "Assignment Grading Assistant","Student Progress Tracker","Course Outline Generator",
    "Parent Communication Bot","Plagiarism Detection Agent","Curriculum Alignment Checker",
    "Scholarship Matcher","Attendance Anomaly Detector","Learning Path Recommender",
    "Exam Question Generator","Enrollment Funnel Tracker","Tutoring Session Scheduler",
    "Grade Dispute Handler","Library Resource Recommender","Campus Event Promoter",
    "Financial Aid Status Bot","Alumni Engagement Agent","Faculty Workload Balancer",
    "Accreditation Document Compiler","Student Mental Health Check-In Bot",
  ],
  "Customer Support": [
    "Ticket Triage & Routing Agent","Auto-Response Draft Bot","Customer Churn Predictor",
    "Knowledge Base Updater","Escalation Detector","Multilingual Support Translator",
    "CSAT Survey Analyzer","SLA Breach Alerter","Chat Transcript Summarizer",
    "Refund Eligibility Checker","Duplicate Ticket Merger","First Response Time Optimizer",
    "Agent Performance Coach","Self-Service Deflection Bot","Product Feedback Aggregator",
    "Customer Health Score Monitor","Proactive Outreach Trigger","Call Center Volume Predictor",
    "Support Email Classifier","Contact Center QA Grader",
  ],
  "Operations & Logistics": [
    "Shipment Tracking Agent","Supplier Invoice Matcher","Warehouse Inventory Auditor",
    "Route Optimization Bot","Purchase Order Generator","Quality Control Image Inspector",
    "Fleet Maintenance Scheduler","Returns & Refunds Processor","Demand Forecasting Agent",
    "Vendor Performance Scorer","Customs Documentation Preparer","Carrier Rate Comparator",
    "Last-Mile Delivery Optimizer","Cold Chain Monitor","Pallet Label Generator",
    "Inbound Freight Coordinator","Cross-Dock Scheduler","Stockout Risk Alerter",
    "3PL Invoice Reconciler","Driver HOS Compliance Checker",
  ],
  "Insurance": [
    "Policy Renewal Reminder","Claims Intake Bot","Fraud Claim Detector",
    "Underwriting Data Gatherer","Premium Calculator","Coverage Gap Analyzer",
    "Adjuster Assignment Router","Medical Bill Auditor","Subrogation Opportunity Finder",
    "Compliance Filing Tracker","Risk Score Updater","Loss Run Report Generator",
    "Agent Licensing Monitor","Policyholder Onboarding Bot","Quote Comparison Engine",
    "Claim Status Notifier","Document Collection Automator","Reinsurance Data Aggregator",
    "FNOL Processor","Actuarial Data Validator",
  ],
  "Travel & Hospitality": [
    "Booking Confirmation Bot","Guest Pre-Arrival Coordinator","Loyalty Points Tracker",
    "Flight Delay Notifier","Hotel Upsell Recommender","Review Response Generator",
    "Tour Package Personalizer","Visa Requirement Checker","Group Travel Coordinator",
    "Dynamic Pricing Monitor","Lost Luggage Tracker","Restaurant Reservation Manager",
    "Housekeeping Schedule Optimizer","Corporate Travel Policy Enforcer","Cancellation Handler",
    "Concierge Recommendation Bot","Travel Insurance Advisor","Event Venue Sourcer",
    "Airport Transfer Coordinator","Guest Feedback Analyzer",
  ],
  "Food & Beverage": [
    "Menu Optimization Agent","Ingredient Supplier Monitor","Food Cost Calculator",
    "Health Inspection Prep Bot","Recipe Scaling Automator","Allergen Alert Checker",
    "Table Reservation Manager","Catering Quote Generator","Delivery Route Optimizer",
    "Inventory Spoilage Alerter","Nutritional Label Generator","Staff Tip Calculator",
    "Daily Special Promoter","Franchise Compliance Checker","Food Waste Tracker",
    "Seasonal Menu Suggester","POS Data Reconciler","Customer Dietary Preference Tracker",
    "Food Safety Incident Logger","Vendor Contract Renewal Bot",
  ],
  "Manufacturing": [
    "Production Schedule Optimizer","Machine Downtime Alerter","Work Order Generator",
    "Defect Rate Tracker","Preventive Maintenance Scheduler","Bill of Materials Validator",
    "Shift Handover Summarizer","ISO Compliance Checker","Tooling Inventory Monitor",
    "Energy Consumption Analyzer","Scrap Rate Reporter","Supplier Lead Time Tracker",
    "OEE Dashboard Bot","Safety Incident Logger","Capacity Utilization Monitor",
    "Prototype Feedback Collector","Quality Assurance Auditor","ERP Data Sync Bot",
    "Warranty Claim Processor","Shop Floor Communication Bot",
  ],
  "Construction & Architecture": [
    "Project Milestone Tracker","Subcontractor Bid Aggregator","Permit Application Monitor",
    "Daily Progress Report Generator","Safety Inspection Scheduler","Change Order Processor",
    "Material Quantity Estimator","Blueprint Version Controller","RFI Response Tracker",
    "Equipment Utilization Monitor","Punch List Manager","Lien Waiver Collector",
    "Weather Delay Notifier","Labor Cost Tracker","Site Photo Organizer",
    "Warranty Document Compiler","Contractor License Verifier","Budget Overrun Alerter",
    "Closeout Document Bot","Zoning Compliance Checker",
  ],
  "Media & Entertainment": [
    "Content Calendar Scheduler","Audience Sentiment Analyzer","Rights & Licensing Tracker",
    "Streaming Performance Monitor","Talent Contract Summarizer","Press Release Distributor",
    "Subtitle Generator","Ad Revenue Reconciler","Social Clip Creator",
    "Fan Engagement Bot","Podcast Episode Promoter","Box Office Predictor",
    "Script Coverage Summarizer","Music Royalty Calculator","Content Moderation Bot",
    "Merchandise Inventory Tracker","Award Submission Coordinator","Live Event Scheduler",
    "Newsletter Growth Tracker","Cross-Platform Analytics Aggregator",
  ],
  "Nonprofit & Philanthropy": [
    "Donor Retention Monitor","Grant Application Tracker","Volunteer Coordinator Bot",
    "Fundraising Campaign Analyzer","Impact Report Generator","Donation Receipt Sender",
    "Event RSVP Manager","Donor Segmentation Agent","In-Kind Donation Logger",
    "Board Meeting Minutes Summarizer","Gift Acknowledgment Automator","Program Outcome Tracker",
    "Major Gift Prospect Identifier","Recurring Donor Health Monitor","Corporate Sponsorship Tracker",
    "Crowdfunding Update Bot","Matching Gift Verifier","Annual Report Compiler",
    "Beneficiary Feedback Collector","501(c)(3) Compliance Checker",
  ],
  "Government & Public Sector": [
    "Permit Workflow Automator","Citizen Service Request Router","FOIA Request Tracker",
    "Budget Allocation Monitor","Public Comment Aggregator","Compliance Audit Scheduler",
    "Contract Procurement Bot","Government Grant Tracker","Constituent Communication Bot",
    "Election Filing Deadline Alerter","Policy Change Notifier","Infrastructure Maintenance Scheduler",
    "Public Records Organizer","Case Management Automator","Tax Delinquency Notifier",
    "License Renewal Reminder","RFP Response Compiler","Emergency Alert Dispatcher",
    "Social Services Eligibility Checker","Interagency Data Sync Bot",
  ],
  "Pharmaceuticals & Biotech": [
    "Clinical Trial Data Aggregator","Regulatory Submission Tracker","Drug Interaction Checker",
    "Lab Sample Chain-of-Custody Bot","Patent Expiry Monitor","Adverse Event Reporter",
    "Formulation Documentation Organizer","FDA 483 Response Drafter","Clinical Protocol Deviation Alerter",
    "Batch Release Checklist Bot","Supply Chain Cold Chain Monitor","Medical Affairs Content Reviewer",
    "Pharmacovigilance Signal Detector","CMC Documentation Compiler","Clinical Site Activation Tracker",
    "Publication Tracker","Competitive Intelligence Monitor","Drug Pricing Analyzer",
    "Biomarker Data Processor","GMP Audit Scheduler",
  ],
  "Automotive": [
    "Vehicle Service Reminder","Recall Campaign Notifier","Dealer Inventory Monitor",
    "Warranty Claims Processor","Test Drive Scheduler","Finance Rate Comparator",
    "Parts Order Tracker","Customer Trade-In Appraiser","Dealership Lead Scorer",
    "Fleet Telematics Analyzer","EV Charging Station Finder","Vehicle Registration Renewal Bot",
    "Insurance Quote Comparator","Accident Report Compiler","Lease End Coordinator",
    "VIN Decoder Bot","Sales Commission Calculator","DMS Data Sync Agent",
    "Customer Satisfaction Follow-Up Bot","Parts Catalog Updater",
  ],
  "Telecommunications": [
    "Network Outage Alerter","Customer Churn Predictor","Plan Upgrade Recommender",
    "Tower Maintenance Scheduler","Billing Dispute Handler","Data Usage Alerter",
    "SIM Activation Bot","Device Trade-In Processor","Contract Renewal Reminder",
    "Roaming Charge Notifier","Ticket Escalation Router","Spectrum License Tracker",
    "Regulatory Compliance Reporter","Network Performance Analyzer","Fraud Call Detector",
    "Fiber Rollout Tracker","Customer Lifetime Value Calculator","MVNO Reconciliation Bot",
    "Self-Install Guide Bot","Telecom Expense Audit Agent",
  ],
  "Energy & Utilities": [
    "Meter Reading Validator","Outage Detection & Alerter","Energy Consumption Analyzer",
    "Renewable Energy Forecast Bot","Grid Load Balancer","Customer Bill Estimator",
    "Pipeline Inspection Scheduler","Safety Incident Reporter","Regulatory Filing Automator",
    "Demand Response Coordinator","Solar Panel Performance Monitor","Carbon Emission Calculator",
    "Utility Rate Change Notifier","Work Order Dispatcher","Transformer Maintenance Scheduler",
    "Fuel Inventory Monitor","Asset Lifecycle Tracker","Customer Energy Audit Bot",
    "Tariff Compliance Checker","Smart Meter Anomaly Detector",
  ],
  "Agriculture & AgriTech": [
    "Crop Yield Predictor","Soil Health Monitor","Irrigation Schedule Optimizer",
    "Pest & Disease Alert Bot","Harvest Window Advisor","Livestock Health Tracker",
    "Supply Chain Traceability Agent","Weather Impact Analyzer","Fertilizer Usage Calculator",
    "Farm Equipment Maintenance Bot","Market Price Monitor","Subsidy Eligibility Checker",
    "Carbon Credit Calculator","Seed Inventory Manager","Field Sensor Data Aggregator",
    "Planting Calendar Generator","Export Compliance Checker","Labor Scheduling Bot",
    "Drone Survey Analyzer","Crop Insurance Claim Bot",
  ],
  "Aerospace & Defense": [
    "MRO Work Order Generator","Parts Traceability Tracker","Airworthiness Directive Monitor",
    "Mission Planning Assistant","Supplier Qualification Checker","ITAR Compliance Bot",
    "Test Flight Data Analyzer","Maintenance Log Summarizer","Defense Contract Tracker",
    "Export License Monitor","Fleet Availability Optimizer","Incident Investigation Logger",
    "Certification Deadline Alerter","Technical Manual Updater","Inventory Obsolescence Detector",
    "Bid Proposal Compiler","Safety Data Sheet Manager","Sub-System Integration Tracker",
    "Ground Support Equipment Scheduler","Configuration Management Bot",
  ],
  "Cybersecurity": [
    "Threat Intelligence Aggregator","Vulnerability Scan Scheduler","Phishing Simulation Bot",
    "Incident Response Coordinator","SIEM Alert Triage Agent","Access Review Automator",
    "Patch Compliance Monitor","Dark Web Mention Alerter","Security Awareness Training Bot",
    "Zero-Day Notification Forwarder","Firewall Rule Auditor","SOC Ticket Prioritizer",
    "GDPR Breach Notifier","Endpoint Anomaly Detector","Pen Test Report Summarizer",
    "Cloud Misconfiguration Detector","Identity Risk Scorer","Log Correlation Agent",
    "Vendor Security Questionnaire Bot","Policy Exception Tracker",
  ],
  "Supply Chain & Procurement": [
    "Purchase Order Approval Bot","Supplier Risk Scorer","Contract Expiry Alerter",
    "RFQ Aggregator","Spend Analysis Agent","Demand Signal Processor",
    "Vendor Onboarding Automator","Invoice 3-Way Match Bot","Lead Time Tracker",
    "Shortage Risk Alerter","Tariff Impact Analyzer","Supplier Diversity Tracker",
    "Goods Receipt Notifier","Procurement KPI Reporter","Category Spend Monitor",
    "Tail Spend Identifier","Catalog Price Updater","Blanket Order Monitor",
    "Supplier Scorecard Generator","Customs Duty Calculator",
  ],
  "Event Management": [
    "Attendee Registration Bot","Speaker Coordination Agent","Venue Sourcing Automator",
    "Event Budget Tracker","Sponsor Management Bot","On-Site Check-In Processor",
    "Post-Event Survey Sender","Session Schedule Optimizer","Badge Generation Bot",
    "A/V Equipment Coordinator","Catering Headcount Updater","Virtual Event Tech Support Bot",
    "Social Media Live Coverage Bot","Exhibitor Onboarding Automator","Travel Reimbursement Processor",
    "Event App Content Updater","Run-of-Show Generator","Guest List Manager",
    "Feedback Sentiment Analyzer","Event ROI Calculator",
  ],
  "Sports & Fitness": [
    "Athlete Performance Tracker","Injury Risk Predictor","Training Schedule Generator",
    "Game Film Analysis Bot","Team Roster Manager","Nutrition Plan Recommender",
    "Fan Engagement Agent","Ticket Sales Monitor","Sponsorship ROI Tracker",
    "Wearable Data Aggregator","Recovery Protocol Advisor","Draft Scouting Report Generator",
    "Gym Membership Renewal Bot","Class Booking Optimizer","Coach Feedback Summarizer",
    "Sports Compliance Checker","Media Rights Tracker","Youth League Coordinator Bot",
    "Fantasy Sports Data Aggregator","Stadium Operations Scheduler",
  ],
  "Beauty & Wellness": [
    "Appointment Booking Bot","Product Restock Alerter","Client Skin Profile Tracker",
    "Loyalty Rewards Manager","Review Response Generator","Consultation Follow-Up Bot",
    "Inventory Expiry Monitor","Staff Commission Calculator","Upsell Recommendation Agent",
    "Patch Test Reminder Bot","Before/After Photo Organizer","Membership Renewal Notifier",
    "Gift Card Tracker","Social Content Scheduler","Ingredient Safety Checker",
    "Seasonal Promotion Bot","Online Store Sync Agent","Training Certification Tracker",
    "Referral Program Manager","Customer Birthday Reward Sender",
  ],
  "Fashion & Apparel": [
    "Trend Forecasting Agent","Inventory Markdown Optimizer","Size Run Planner",
    "Influencer Gifting Tracker","Collection Launch Coordinator","Return Rate Analyzer",
    "Fabric Sourcing Monitor","Lookbook Content Generator","Drop Shipping Sync Bot",
    "Celebrity Sighting Tracker","Wholesale Order Manager","Fit Feedback Aggregator",
    "Fashion Week Schedule Bot","Production Sample Tracker","Sustainability Score Calculator",
    "Clearance Sale Optimizer","Retail Sell-Through Monitor","Brand Partnership Coordinator",
    "SKU Rationalization Bot","Customer Style Profile Builder",
  ],
  "Accounting & Tax": [
    "Bank Reconciliation Bot","Accounts Payable Aging Monitor","Chart of Accounts Cleaner",
    "Tax Deadline Reminder","1099 Preparation Agent","Expense Report Reviewer",
    "Fixed Asset Depreciation Calculator","Audit Trail Generator","Monthly Close Checklist Bot",
    "Sales Tax Filing Automator","Transfer Pricing Document Compiler","Intercompany Reconciliation Agent",
    "Cash Flow Forecast Bot","Payroll Tax Reconciler","Revenue Recognition Checker",
    "Multi-Entity Consolidation Agent","Accounting Policy Updater","Deferred Revenue Tracker",
    "Accrual Journal Entry Bot","Tax Credit Identifier",
  ],
  "Venture Capital & Private Equity": [
    "Deal Flow Tracker","Portfolio Company Monitor","LP Communication Bot",
    "Cap Table Manager","Due Diligence Checklist Automator","Valuation Model Updater",
    "Board Meeting Prep Agent","Competitive Landscape Analyzer","Exit Scenario Modeler",
    "Co-Investor Tracker","Fund Deployment Monitor","Management Team Scorer",
    "Term Sheet Generator","KPI Dashboard Aggregator","Follow-On Investment Alerter",
    "ESG Compliance Tracker","Deal Sourcing Scraper","Covenant Breach Monitor",
    "Investor Reporting Automator","Fundraising Pipeline Manager",
  ],
  "SaaS & Tech Startups": [
    "MRR/ARR Dashboard Bot","Churn Prediction Agent","Feature Request Aggregator",
    "Sprint Planning Summarizer","Bug Triage Prioritizer","Customer Onboarding Flow Bot",
    "Usage Analytics Reporter","Trial-to-Paid Conversion Tracker","API Health Monitor",
    "Product Roadmap Communicator","NPS Survey Automator","Pricing Page A/B Tester",
    "Support Ticket to Feature Linker","Deployment Notifier","SLA Uptime Reporter",
    "Developer Documentation Updater","Investor Update Generator","Sales Demo Scheduler",
    "Freemium Upgrade Trigger Bot","Cohort Retention Analyzer",
  ],
  "Healthcare IT": [
    "EHR Data Migration Validator","HL7 Message Processor","FHIR API Integration Bot",
    "Patient Portal Adoption Tracker","Interoperability Gap Analyzer","Downtime Notification Bot",
    "HIPAA Risk Assessment Scheduler","Clinical Workflow Optimizer","Payer Integration Tester",
    "System Downtime Root Cause Analyzer","User Access Provisioning Bot","Clinical Decision Support Alerter",
    "Health Data Quality Monitor","Interface Engine Monitor","IT Help Desk Triager",
    "Telemedicine Platform Monitor","EHR Training Scheduler","Vendor Patch Compliance Tracker",
    "Data Breach Response Coordinator","Clinical Reporting Automator",
  ],
  "Mental Health & Therapy": [
    "Session Reminder Bot","Mood Tracking Check-In Agent","Therapist Availability Scheduler",
    "Crisis Resource Dispatcher","Treatment Plan Documenter","Insurance Benefit Verifier",
    "Progress Note Generator","Group Therapy Coordinator","Burnout Risk Monitor",
    "Medication Adherence Nudger","Psychoeducation Content Sender","Waitlist Manager",
    "Outcome Measure Collector","Billing & Coding Assistant","Teletherapy Tech Support Bot",
    "Cultural Competency Matcher","Trauma-Informed Intake Bot","Safety Plan Reminder",
    "Provider Supervision Scheduler","Client Feedback Aggregator",
  ],
  "Environmental & Sustainability": [
    "Carbon Footprint Calculator","ESG Reporting Automator","Energy Audit Scheduler",
    "Renewable Certificate Tracker","Waste Diversion Monitor","Water Usage Analyzer",
    "Scope 3 Emission Aggregator","Green Building Certification Tracker","Sustainability Goal Monitor",
    "Biodiversity Impact Assessor","Circular Economy Tracker","Climate Risk Alerter",
    "Supply Chain Emissions Calculator","EV Fleet Transition Planner","Carbon Offset Verifier",
    "Regulatory Compliance Reporter","Net Zero Roadmap Generator","Plastic Reduction Tracker",
    "Environmental Incident Logger","Stakeholder Sustainability Report Generator",
  ],
  "Property Management": [
    "Rent Collection Reminder","Maintenance Work Order Dispatcher","Lease Expiry Tracker",
    "Tenant Onboarding Bot","Property Inspection Scheduler","Utility Bill Reconciler",
    "Vendor Invoice Approver","Vacancy Marketing Automator","Late Fee Calculator",
    "Move-Out Deposit Processor","HOA Fee Collector","Common Area Maintenance Scheduler",
    "Tenant Communication Bot","Delinquency Escalation Agent","Insurance Renewal Tracker",
    "Property Tax Appeal Assistant","Energy Efficiency Monitor","Parking Permit Manager",
    "Annual Rent Increase Notifier","Contractor License Verifier",
  ],
  "Recruiting Agencies": [
    "Job Order Intake Bot","Candidate Sourcing Automator","Interview Feedback Collector",
    "Client Check-In Scheduler","Offer Negotiation Tracker","Placement Revenue Calculator",
    "Contractor Timesheet Processor","Candidate Reengagement Bot","Skills Gap Analyzer",
    "Job Board Posting Automator","Reference Check Coordinator","Headhunting Outreach Agent",
    "Requisition Status Reporter","Diversity Placement Tracker","Contract Staffing Compliance Bot",
    "Candidate Rejection Emailer","Client Satisfaction Surveyor","Time-to-Fill Analyzer",
    "Bench Candidate Manager","Employer Brand Content Bot",
  ],
  "Consulting": [
    "Project Scope Generator","Client Meeting Summarizer","Deliverable Deadline Tracker",
    "Utilization Rate Monitor","Statement of Work Drafter","Change Request Handler",
    "Thought Leadership Content Bot","RFP Response Compiler","Knowledge Base Article Creator",
    "Proposal Win/Loss Analyzer","Billing Rate Optimizer","Expert Network Matcher",
    "Client Feedback Aggregator","Practice Area Performance Reporter","Engagement Risk Alerter",
    "Resource Allocation Planner","Benchmark Data Aggregator","Competitive Differentiator Bot",
    "Retainer Renewal Reminder","Case Study Generator",
  ],
  "Research & Analytics": [
    "Literature Review Compiler","Survey Response Analyzer","Data Source Aggregator",
    "Statistical Anomaly Detector","Report Formatting Bot","Citation Manager",
    "A/B Test Monitor","Market Sizing Calculator","Trend Signal Identifier",
    "Focus Group Transcript Summarizer","Competitor Intelligence Tracker","Research Proposal Generator",
    "Data Quality Validator","Panel Management Bot","Cross-Tab Analyzer",
    "Insight Narrative Generator","Dashboard Data Refresher","Patent Landscape Mapper",
    "Social Listening Aggregator","NLP Coding Bot",
  ],
  "Creative Agencies": [
    "Creative Brief Generator","Asset Version Controller","Client Approval Workflow Bot",
    "Production Timeline Tracker","Brand Consistency Checker","Stock Asset Sourcer",
    "Copywriting Variant Generator","Color Palette Suggester","Font Pairing Recommender",
    "Campaign Naming Bot","Mood Board Compiler","Social Ad Resizer",
    "Print Spec Validator","Vendor Quote Aggregator","Creative Feedback Summarizer",
    "Award Entry Compiler","Retainer Usage Tracker","New Business Proposal Bot",
    "Content Repurposing Agent","Photography Brief Generator",
  ],
  "Dental Practices": [
    "Patient Appointment Reminder","Insurance Eligibility Verifier","Treatment Plan Presenter",
    "Recall Scheduling Bot","X-Ray Organization Agent","Billing Code Auditor",
    "Patient Review Requester","New Patient Onboarding Bot","Referral Thank-You Sender",
    "Lab Case Tracker","Supply Order Monitor","No-Show Follow-Up Bot",
    "HIPAA Training Scheduler","Payment Plan Manager","End-of-Day Report Generator",
    "Continuing Education Tracker","Emergency Slot Coordinator","Sterilization Log Bot",
    "Insurance Claim Resubmitter","Staff Performance Tracker",
  ],
  "Veterinary & Pet Care": [
    "Pet Vaccination Reminder","Appointment Booking Bot","Pet Health Record Organizer",
    "Prescription Refill Notifier","Boarding Availability Tracker","Pet Insurance Claim Helper",
    "Surgery Follow-Up Coordinator","Flea & Tick Season Alerter","Client Re-engagement Bot",
    "Grooming Schedule Manager","Nutrition Recommendation Agent","Microchip Registration Bot",
    "Emergency Triage Helper","Lab Result Notifier","Dental Health Reminder",
    "New Patient Welcome Bot","Inventory Reorder Alerter","Multi-Pet Household Coordinator",
    "Vet Referral Tracker","End-of-Life Support Resource Sender",
  ],
  "Publishing & Journalism": [
    "Editorial Calendar Manager","Fact-Check Assistant","Source Verification Bot",
    "Article Pitch Tracker","SEO Headline Optimizer","Subscription Renewal Notifier",
    "Author Payment Calculator","Rights & Permissions Tracker","Comment Moderation Bot",
    "Newsletter A/B Tester","Print Deadline Alerter","Journalist Assignment Scheduler",
    "Advertiser Insertion Order Processor","Headline Performance Analyzer","Social Amplification Bot",
    "Audience Segment Reporter","Grammar & Style Checker","Press Release Distributor",
    "Podcast Transcript Generator","Breaking News Alert Bot",
  ],
  "Gaming & Esports": [
    "Player Matchmaking Optimizer","In-Game Event Scheduler","Toxic Behavior Detector",
    "Cheat Detection Alerter","Tournament Bracket Generator","Streaming Performance Monitor",
    "In-App Purchase Analyzer","Game Bug Report Aggregator","Player Retention Bot",
    "Esports Roster Manager","Sponsor Activation Tracker","Fan Community Moderator",
    "Game Update Notifier","Player Leaderboard Publisher","Prize Pool Calculator",
    "Live Commentary Summarizer","Beta Tester Feedback Collector","Revenue Churn Analyzer",
    "Game Localization Validator","Player Lifetime Value Calculator",
  ],
  "Blockchain & Web3": [
    "Smart Contract Auditor","Wallet Activity Monitor","Gas Fee Optimizer",
    "Token Launch Coordinator","DAO Governance Notifier","NFT Mint Tracker",
    "DeFi Protocol Health Monitor","On-Chain Analytics Aggregator","KYC/AML Compliance Bot",
    "Liquidity Pool Alerter","Crypto Portfolio Rebalancer","Airdrop Eligibility Checker",
    "Bridge Transaction Monitor","Protocol Upgrade Notifier","Community Discord Moderator",
    "Staking Reward Calculator","Regulatory News Alerter","Token Vesting Tracker",
    "Whitelist Management Bot","Crypto Tax Report Generator",
  ],
  "Retail Banking": [
    "Account Opening Workflow Bot","Customer Financial Health Scorer","Cross-Sell Trigger Agent",
    "ATM Outage Notifier","Branch Traffic Predictor","Digital Banking Adoption Tracker",
    "Safe Deposit Box Reminder","Overdraft Protection Alerter","Wire Transfer Status Bot",
    "Dormant Account Reactivator","New Product Launch Announcer","Customer Lifetime Value Predictor",
    "Debit Card Dispute Handler","Savings Goal Coach","Financial Literacy Content Bot",
    "Bank Statement Reconciler","High-Value Customer Identifier","Chatbot Handoff Coordinator",
    "Loan Top-Up Reminder","Rate Comparison Bot",
  ],
  "Logistics & Warehousing": [
    "Receiving Dock Scheduler","Bin Location Optimizer","Pick & Pack Error Detector",
    "Cycle Count Coordinator","Dangerous Goods Compliance Checker","Packing Slip Generator",
    "Inbound PO Tracker","Return Merchandise Authorizer","Barcode Label Printer Bot",
    "Freight Bill Auditor","Cross-Border Documentation Preparer","Carrier Performance Tracker",
    "Storage Utilization Monitor","Load Planning Optimizer","Inbound Quality Inspector",
    "Seasonal Surge Planner","Rate Card Updater","Third-Party Audit Scheduler",
    "On-Time Delivery Reporter","Palletization Advisor",
  ],
  "Social Media & Content": [
    "Content Repurposing Agent","Hashtag Performance Tracker","Engagement Rate Monitor",
    "Comment Sentiment Analyzer","Posting Schedule Optimizer","Influencer Collaboration Tracker",
    "Viral Content Detector","Brand Mention Aggregator","Cross-Platform Publisher",
    "Audience Growth Reporter","Story Idea Generator","Video Caption Writer",
    "Profile Audit Bot","Social Ad Copy Tester","Community Manager Helper",
    "UGC Rights Requester","Content Performance Benchmarker","Crisis Response Drafter",
    "Profile Consistency Checker","Social Competitor Analyzer",
  ],
};

interface AgentsModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (agent: string) => void;
  onStartChat?: () => void;
}

export function AgentsModal({ open, onClose, onSelect, onStartChat }: AgentsModalProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return AGENTS_BY_INDUSTRY;
    const q = query.toLowerCase();
    const result: Record<string, string[]> = {};
    for (const [industry, agents] of Object.entries(AGENTS_BY_INDUSTRY)) {
      const matches = agents.filter((a) => a.toLowerCase().includes(q));
      if (matches.length > 0) result[industry] = matches;
    }
    return result;
  }, [query]);

  const hasResults = Object.keys(filtered).length > 0;
  const totalResults = Object.values(filtered).reduce((sum, arr) => sum + arr.length, 0);
  const totalAgents = Object.values(AGENTS_BY_INDUSTRY).reduce((sum, arr) => sum + arr.length, 0);
  const totalIndustries = Object.keys(AGENTS_BY_INDUSTRY).length;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="agents-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            key="agents-panel"
            initial={{ opacity: 0, scale: 0.95, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={{ duration: 0.2 }}
            className="relative scifi-wave-bg rounded-2xl w-full max-w-2xl flex flex-col overflow-hidden border border-cyan-400/25"
            style={{
              maxHeight: "85vh",
              boxShadow: "0 24px 80px rgba(0,0,0,0.55), 0 0 40px rgba(0,229,255,0.12)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="shrink-0 px-6 pt-5 pb-4 border-b border-cyan-400/15 bg-black/40 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base font-semibold text-white">Agent Library</h2>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {totalAgents.toLocaleString()} agents across {totalIndustries} industries
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-full hover:bg-cyan-400/10 transition-colors text-slate-400 hover:text-cyan-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {/* Search */}
              <div className="flex items-center gap-2 bg-black/50 border border-cyan-400/20 focus-within:border-cyan-400/60 focus-within:ring-2 focus-within:ring-cyan-400/30 rounded-full px-4 py-2 transition-all">
                <Search className="w-4 h-4 text-cyan-300 shrink-0" />
                <input
                  type="text"
                  aria-label="Search agents"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search agents…"
                  className="flex-1 bg-transparent border-none outline-none text-sm text-white placeholder:text-slate-500"
                  autoFocus
                />
                {query && (
                  <button onClick={() => setQuery("")} className="text-slate-400 hover:text-cyan-300">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {query && hasResults && (
                <p className="text-xs text-slate-400 mt-2 px-1">
                  {totalResults} result{totalResults !== 1 ? "s" : ""} for "{query}"
                </p>
              )}
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 px-6 py-4 bg-black/30">
              {hasResults ? (
                <div className="flex flex-col gap-6">
                  {Object.entries(filtered).map(([industry, agents]) => (
                    <div key={industry}>
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-cyan-300 mb-2">
                        {industry}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {agents.map((agent) => {
                          const q = query.trim().toLowerCase();
                          const idx = q ? agent.toLowerCase().indexOf(q) : -1;
                          return (
                            <div
                              key={agent}
                              className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-black/40 border border-cyan-400/10 hover:bg-cyan-400/10 hover:border-cyan-400/40 transition-colors text-sm text-slate-200"
                            >
                              <Bot className="w-3.5 h-3.5 shrink-0 text-cyan-300/70 group-hover:text-cyan-300 transition-colors" />
                              <span className="flex-1 leading-snug">
                                {idx >= 0 ? (
                                  <>
                                    {agent.slice(0, idx)}
                                    <mark className="bg-fuchsia-400/30 text-white rounded px-0.5">
                                      {agent.slice(idx, idx + q.length)}
                                    </mark>
                                    {agent.slice(idx + q.length)}
                                  </>
                                ) : agent}
                              </span>
                              <button
                                onClick={() => onSelect(agent)}
                                title={`Start chat: ${agent}`}
                                className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 text-cyan-300 hover:text-cyan-200"
                              >
                                <PlayCircle className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-cyan-400/10 border border-cyan-400/40 flex items-center justify-center" style={{ boxShadow: "0 0 24px rgba(0,229,255,0.25)" }}>
                    <Bot className="w-7 h-7 text-cyan-300" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-white">No agent found for "{query}"</p>
                    <p className="text-sm text-slate-400 max-w-xs leading-relaxed">
                      That's exactly why we're here —<br />
                      <span className="font-semibold text-cyan-300">to build ANYTHING YOU NEED.</span>
                    </p>
                  </div>
                  <button
                    onClick={() => { onStartChat?.(); }}
                    className="mt-1 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cyan-400 hover:bg-cyan-300 active:scale-95 text-slate-950 text-sm font-bold transition-all"
                    style={{ boxShadow: "0 0 24px rgba(0,229,255,0.45)" }}
                  >
                    <PlayCircle size={16} />
                    Start chat on main screen
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
